'use client';

import React, { useEffect, useRef } from 'react';

const services = [
  {
    num: '01',
    title: 'SAC Planning Implementation',
    duration: '12–20 weeks',
    desc: 'End-to-end implementation support from architecture design through production deployment. Covers all 7 layers of the SAC Planning Architecture — from data model design to planning experience and governance.',
    deliverables: [
      'Planning architecture design document',
      'Data model and dimension design',
      'Integration design and build',
      'Planning logic and data actions',
      'Planning stories and input forms',
      'Security role configuration',
      'User acceptance testing support',
      'Go-live and hypercare support',
    ],
  },
  {
    num: '02',
    title: 'Planning Architecture Advisory',
    duration: '4–6 weeks',
    desc: 'Design the planning architecture before build begins. Prevents costly rework by establishing the right model structure, data architecture, and integration approach from the outset.',
    deliverables: [
      'Planning model design specification',
      'Dimension and hierarchy design',
      'Version strategy documentation',
      'Integration architecture design',
      'Planning logic design patterns',
    ],
  },
  {
    num: '03',
    title: 'Planning Model Optimisation',
    duration: '4–8 weeks',
    desc: 'Review and improve an existing SAC Planning implementation. Identify structural issues, logic inefficiencies, and governance gaps. Deliver a prioritised remediation roadmap.',
    deliverables: [
      'Model architecture review',
      'Performance assessment',
      'Logic and data action review',
      'Governance gap analysis',
      'Prioritised remediation plan',
    ],
  },
  {
    num: '04',
    title: 'Planning Performance Tuning',
    duration: '2–4 weeks',
    desc: 'Targeted engagement to diagnose and resolve performance issues in SAC Planning models — slow data actions, unresponsive planning stories, and integration bottlenecks.',
    deliverables: [
      'Performance diagnostic report',
      'Data action optimisation',
      'Model structure recommendations',
      'Story design improvements',
      'Integration performance fixes',
    ],
  },
  {
    num: '05',
    title: 'Planning Governance Setup',
    duration: '3–5 weeks',
    desc: 'Establish the governance framework for a SAC Planning program — security roles, version management, change control, and Planning Centre of Excellence operating model.',
    deliverables: [
      'Security role design document',
      'Version management framework',
      'Planning calendar design',
      'Change control process',
      'Planning COE operating model',
    ],
  },
  {
    num: '06',
    title: 'SAC Planning Training',
    duration: '1–3 weeks',
    desc: 'Role-based training for planning teams — covering model administration, data action management, planning story design, and end-user planning input.',
    deliverables: [
      'Administrator training program',
      'Planner training materials',
      'Data action design workshop',
      'Planning story design training',
      'Training documentation package',
    ],
  },
  {
    num: '07',
    title: 'Planning Design Workshops',
    duration: '1–5 days',
    desc: 'Facilitated workshops to align stakeholders on planning architecture, driver design, dimension structure, or governance framework. Structured outputs, not open-ended discussions.',
    deliverables: [
      'Workshop facilitation',
      'Structured design output document',
      'Decision register',
      'Next steps and owners',
    ],
  },
];

export default function ServicesList() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.02 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border">
      <div className="max-w-container mx-auto">
        <div className="mono-label mb-8 reveal-on-scroll">Services</div>

        <div className="flex flex-col gap-0">
          {services.map((service, i) => (
            <div
              key={service.num}
              className={`service-card reveal-on-scroll reveal-delay-${Math.min(i + 1, 6)} border border-border mb-3 p-8 bg-white`}
              style={{ borderRadius: '4px' }}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-1">
                  <span className="mono-label">{service.num}</span>
                </div>
                <div className="md:col-span-5">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-heading font-semibold text-foreground text-base">
                      {service.title}
                    </h3>
                    <span className="mono-label">{service.duration}</span>
                  </div>
                  <p className="text-sm text-muted leading-relaxed font-body">{service.desc}</p>
                </div>
                <div className="md:col-span-6">
                  <div className="mono-label mb-3">Typical Deliverables</div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {service.deliverables.map((d) => (
                      <li key={d} className="flex gap-2 text-sm font-body text-foreground">
                        <span className="text-muted shrink-0">—</span>
                        <span className="leading-relaxed text-sm">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}